/**
 * `useSceneFight` — the Director's read of the fight on the table: a read-only encounter socket on
 * the table's encounter (the GM's slot may hold several; COMBAT.app's own is another), folded to
 * the round and side counts the SCENE card shows ({@link fightStatusOf}), the live state the END
 * COMBAT panel plans its roster commit from, and the GM's `end_combat` / `reopen_combat` for it. `status` is null while the encounter loads. Mount it only while a fight is
 * on the table (`SceneFightReader` in `GmDirectivePanel`), keyed by the encounter id.
 */
import { isCombatOver, type EncounterState } from "@lancer-vtt/domain";
import type { DispatchResult } from "../../lib/roster/remoteRosterController";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { useEncounter } from "../../lib/encounter/useEncounter";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import { fightStatusOf, type FightStatus } from "../../lib/game/sceneStatus";

export type SceneFight = {
  status: FightStatus | null;
  /** The fight's live state (what the END COMBAT panel plans from); null while it loads. */
  state: EncounterState | null;
  /** End the fight (`end_combat`), the END COMBAT panel's `onEnd`. */
  end: () => DispatchResult;
  /** Reopen an ended fight (`reopen_combat`). */
  reopen: () => DispatchResult;
};

/** A narrative table: no fight to read or end. */
const NOT_A_FIGHT: DispatchResult = { ok: false, error: "no fight on the table" };
export const NO_FIGHT: SceneFight = { status: null, state: null, end: () => NOT_A_FIGHT, reopen: () => NOT_A_FIGHT };

export function useSceneFight(encounterId: string): SceneFight {
  const { compendium } = useCompendiumContext();
  const session = useGameSessionContext();
  const encounter = useEncounter(compendium, encounterId, { token: session.token ?? "", isGm: session.role === "gm" });
  const state = encounter.state;
  const actorId = encounter.actorId ?? "gm";
  return {
    status: state === null || encounter.loading ? null : fightStatusOf(state, isCombatOver(state)),
    state: encounter.loading ? null : state,
    end: () => encounter.dispatch({ type: "end_combat", actorId, at: new Date().toISOString() }),
    reopen: () => encounter.dispatch({ type: "reopen_combat", actorId }),
  };
}
